import { withStyles } from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";
import AlternateEmail from "@material-ui/icons/AlternateEmail";
import ArrowRight from "@material-ui/icons/ArrowRight";
import * as PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import GhostChip from "../../composer/GhostChip";
import ListPicker from "../../ListPicker";

const styles = (theme) => ({
  label: {
    position: "relative",
  },
  nowrap: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
  },
  chipIcon: {
    marginLeft: 0,
  },
  chipSingle: {
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
  },
  chipRoot: {
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(1) - theme.spacing(0.25),
    marginBottom: theme.spacing(1) - theme.spacing(0.25),
    cursor: "pointer",
  },
  borderBottom: {
    borderBottom: `solid 1px ${theme.palette.grey[500]}`,
    paddingBottom: "1px",
    "&:hover": {
      borderBottom: "solid 2px",
      paddingBottom: 0,
    },
  },
  addButton: {
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(1),
    marginLeft: 0,
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
  },
});

const WrappedChipListPicker = ({
  classes,
  className,
  input: { onChange, onBlur, value, name },
  label,
  hideLabel,
  description,
  datasource,
  clearable,
  margin,
  helperText,
  disabled,
  required,
  renderIcon,
  isMulti,
  renderLabel,
  renderFilter,
  filterInitialValues,
  submitOnChange,
  selectAll,
  toOption,
  fromOption,
  hideUnderline,
  addIcon: AddIcon,
  meta: { touched, error, invalid },
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (value === "") {
      // ignore
    } else if (value === null || value === undefined) {
      onBlur(undefined);
    } else {
      onBlur(value);
    }
  }, [value]);

  const onSelect = (values) => {
    setDialogOpen(false);

    onChange(values);
  };

  const getSelection = (values) => {
    if (values === null || values === undefined || values === "") {
      return null;
    }
    return values;
  };

  const onDelete = (option) => {
    if (isMulti) {
      return () => {
        const newValues = value.filter((v) => v !== option);
        onChange(newValues);
      };
    }
    return () => setDialogOpen(true);
  };

  const valueAsList = value && !isMulti ? [value] : value;

  return (
    <FormControl
      className={className}
      fullWidth
      margin={margin}
      name={`position-${name}`}
    >
      {!hideLabel && (
        <InputLabel
          className={classes.label}
          shrink
          required={required}
          data-cy={label}
        >
          {label}
        </InputLabel>
      )}
      {description && (
        <Typography variant={"caption"} color={"textSecondary"}>
          {description}
        </Typography>
      )}
      <div className={hideUnderline ? null : classes.borderBottom}>
        <div
          className={classes.container}
          onClick={disabled ? null : () => setDialogOpen(true)}
          role="button"
          onKeyPress={disabled ? null : () => setDialogOpen(true)}
          tabIndex={0}
          data-cy="dialog"
        >
          {isMulti && (
            <IconButton
              className={classes.addButton}
              color="inherit"
              disabled={disabled}
              onClick={disabled ? null : () => setDialogOpen(true)}
              data-cy="iconButton"
            >
              <AddIcon />
            </IconButton>
          )}
          {valueAsList &&
            valueAsList.length > 0 &&
            valueAsList.map((v) => (
              <Chip
                key={toOption(v).id}
                className={
                  valueAsList.length > 1 ? classes.chip : classes.chipSingle
                }
                classes={{
                  root: classes.chipRoot,
                  label: classes.nowrap,
                  icon: classes.chipIcon,
                }}
                icon={renderIcon(v, 30)}
                label={toOption(v).label}
                deleteIcon={isMulti ? null : <ArrowRight />}
                onDelete={disabled ? null : onDelete(v)}
                data-cy={toOption(v).label}
              />
            ))}
          {(!valueAsList || valueAsList.length === 0) && !isMulti && (
            <GhostChip
              className={classes.chip}
              label={`Select ${label}`}
              onClick={disabled ? null : () => setDialogOpen(true)}
            />
          )}
        </div>
        <ListPicker
          data-cy={label}
          title={label}
          actionText="Select"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={(values) => onSelect(values)}
          datasource={datasource}
          selected={getSelection(value)}
          isMulti={isMulti}
          selectAll={selectAll}
          clearable={clearable}
          renderIcon={renderIcon}
          renderLabel={renderLabel}
          renderFilter={renderFilter}
          filterInitialValues={filterInitialValues}
          submitOnChange={submitOnChange}
          toOption={toOption}
          fromOption={fromOption}
        />
      </div>
      {touched && error && (
        <FormHelperText error={touched && invalid}>{error}</FormHelperText>
      )}
      {!(touched && error) && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

WrappedChipListPicker.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  input: PropTypes.object.isRequired,
  datasource: PropTypes.oneOfType([PropTypes.func, PropTypes.array]).isRequired,
  meta: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isMulti: PropTypes.bool,
  renderIcon: PropTypes.func.isRequired,
  renderLabel: PropTypes.func,
  renderFilter: PropTypes.func,
  filterInitialValues: PropTypes.object,
  submitOnChange: PropTypes.bool,
  margin: PropTypes.string,
  helperText: PropTypes.any,
  disabled: PropTypes.bool,
  selectAll: PropTypes.bool,
  clearable: PropTypes.bool,
  toOption: PropTypes.func,
  fromOption: PropTypes.func,
  required: PropTypes.bool,
  addIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  hideUnderline: PropTypes.bool,
};

WrappedChipListPicker.defaultProps = {
  className: null,
  isMulti: true,
  renderLabel: undefined,
  renderFilter: undefined,
  filterInitialValues: undefined,
  margin: "dense",
  hideLabel: false,
  helperText: "",
  disabled: false,
  selectAll: false,
  clearable: false,
  toOption: (value) => value,
  fromOption: (option) => option,
  required: false,
  submitOnChange: false,
  addIcon: AlternateEmail,
  hideUnderline: false,
};

export default withStyles(styles)(WrappedChipListPicker);
